import api from '../services/api';

const DOCUMENTATION_URL = '/documentation';

/**
 * Get all documentation pages (admin list)
 */
export const getDocumentation = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.section) params.append('section', filters.section);
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    const url = `/api${DOCUMENTATION_URL}${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation:', error);
    throw error;
  }
};

/**
 * Get active documentation pages only
 */
export const getActiveDocumentation = async () => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/active`);
    return response.data;
  } catch (error) {
    console.error('Error fetching active documentation:', error);
    throw error;
  }
};

/**
 * Get documentation by path (context-aware)
 * @param {string} path - The route path to get documentation for
 * @param {boolean} exactOnly - If true, only match exact path (no parent fallback)
 */
export const getDocumentationByPath = async (path, exactOnly = false) => {
  try {
    let url = `/api${DOCUMENTATION_URL}/path?path=${encodeURIComponent(path)}`;
    if (exactOnly) {
      url += '&exact=1';
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation by path:', error);
    throw error;
  }
};

/**
 * Get all documentation matching a path (multiple results)
 * @param {string} path - The route path to get documentation for
 */
export const getAllDocumentationByPath = async (path) => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/all-by-path?path=${encodeURIComponent(path)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation by path:', error);
    throw error;
  }
};

/**
 * Get documentation grouped by type (page, tooltip, walkthrough, faq)
 * @param {string} path - The route path to get documentation for
 * @param {boolean} exactOnly - If true, only match exact path (no parent fallback)
 * @returns {Object} { page: [], tooltip: [], walkthrough: [], faq: [], counts: {} }
 */
export const getDocumentationGroupedByType = async (path, exactOnly = false) => {
  try {
    let url = `/api${DOCUMENTATION_URL}/grouped?path=${encodeURIComponent(path)}`;
    if (exactOnly) {
      url += '&exact=1';
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching grouped documentation:', error);
    throw error;
  }
};

/**
 * Get documentation by section
 */
export const getDocumentationBySection = async (section) => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/section/${encodeURIComponent(section)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation by section:', error);
    throw error;
  }
};

/**
 * Get single documentation page
 */
export const getDocumentationById = async (id) => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation:', error);
    throw error;
  }
};

/**
 * Create new documentation page
 */
export const createDocumentation = async (data) => {
  try {
    const response = await api.post(`/api${DOCUMENTATION_URL}`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating documentation:', error);
    throw error;
  }
};

/**
 * Update documentation page
 */
export const updateDocumentation = async (id, data) => {
  try {
    const response = await api.put(`/api${DOCUMENTATION_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating documentation:', error);
    throw error;
  }
};

/**
 * Delete documentation page
 */
export const deleteDocumentation = async (id) => {
  try {
    const response = await api.delete(`/api${DOCUMENTATION_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting documentation:', error);
    throw error;
  }
};

/**
 * Search documentation
 */
export const searchDocumentation = async (searchTerm) => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching documentation:', error);
    throw error;
  }
};

/**
 * Get all sections
 */
export const getDocumentationSections = async () => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/sections`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sections:', error);
    throw error;
  }
};

/**
 * Get all documented paths
 */
export const getDocumentedPaths = async () => {
  try {
    const response = await api.get(`/api${DOCUMENTATION_URL}/paths`);
    return response.data;
  } catch (error) {
    console.error('Error fetching paths:', error);
    throw error;
  }
};

/**
 * Upload screenshot for documentation
 */
export const uploadDocumentationScreenshot = async (docId, file, caption = '', order = 0) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    formData.append('order', order);
    
    const response = await api.post(`/api${DOCUMENTATION_URL}/${docId}/screenshots`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading screenshot:', error);
    throw error;
  }
};

/**
 * Delete screenshot
 */
export const deleteDocumentationScreenshot = async (screenshotId) => {
  try {
    const response = await api.delete(`/api${DOCUMENTATION_URL}/screenshots/${screenshotId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting screenshot:', error);
    throw error;
  }
};

/**
 * Get screenshot download URL
 */
export const getScreenshotUrl = (filename) => {
  return `/api${DOCUMENTATION_URL}/screenshots/download/${filename}`;
};

/**
 * Toggle documentation status (active/draft)
 */
export const toggleDocumentationStatus = async (id, newStatus) => {
  try {
    const response = await api.put(`/api${DOCUMENTATION_URL}/${id}`, {
      doc_status: newStatus ? 1 : 0
    });
    return response.data;
  } catch (error) {
    console.error('Error toggling documentation status:', error);
    throw error;
  }
};
